import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SettingsService } from '@app/services/settings/settings.service';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { getOrderSplitIndicesString } from '@utils/order-split-utils';
import { TimeUnits } from '@utils/times-utils';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

const onlyNumbersAndCommas = /^[0-9]+(,[0-9]+)*$/;
type OrdersData = { id: number; orderNumber: number; splitted: boolean; splitIndex: number; splitIndices: number[] };

@Component({
  selector: 'reset-store-dialog',
  templateUrl: './reset-store-dialog.component.html',
  styleUrls: ['./reset-store-dialog.component.scss'],
  standalone: false,
})
export class ResetStoreDialogComponent implements OnInit {
  ordersToReset$: Observable<{ veifyOrders: OrdersData[]; assignOrders: OrdersData[] }>;
  posibbleVerifyOrders = [];
  posibbleAssignOrders = [];
  isLoading = true;

  constructor(
    public dialogRef: MatDialogRef<ResetStoreDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private settingsSrv: SettingsService,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.ordersToReset$ = this.settingsSrv.getOptionalOrdersToReset();
    this.ordersToReset$
      .pipe(first())
      .subscribe((ordersData: { veifyOrders: OrdersData[]; assignOrders: OrdersData[] }) => {
        this.posibbleVerifyOrders = ordersData?.veifyOrders
          ?.filter((order) => {
            return (
              !order.splitted ||
              order.splitIndex == 0 ||
              (order.splitIndices?.length && order.splitIndices.every((index) => index == 0))
            );
          })
          ?.map((order) => {
            const splitIndicesStr = getOrderSplitIndicesString(order as any, 'view');
            return {
              label: `${order.orderNumber}${splitIndicesStr ? `-${splitIndicesStr}` : ''}`,
              value: order.id,
              checked: false,
            } as DropdownOption;
          });
        this.posibbleAssignOrders = ordersData.assignOrders
          ?.filter((order) => {
            return (
              !order.splitted ||
              order.splitIndex == 0 ||
              (order.splitIndices?.length && order.splitIndices.every((index) => index == 0))
            );
          })
          ?.map((order) => {
            const splitIndicesStr = getOrderSplitIndicesString(order as any, 'view');
            return {
              label: `${order.orderNumber}${splitIndicesStr ? `-${splitIndicesStr}` : ''}`,
              value: order.id,
              checked: false,
            };
          });
        this.isLoading = false;
      });
  }

  changeVerifySelected(event: any) {
    this.posibbleVerifyOrders = event;
  }

  changeAssignSelected(event: any) {
    this.posibbleAssignOrders = event;
  }

  cancel() {
    this.dialogRef.close(false);
  }

  confirm() {
    const verifiedOrders = this.posibbleVerifyOrders
      .filter((dropDownOption) => dropDownOption.checked)
      ?.map((order) => order.value);
    const assignedOrders = this.posibbleAssignOrders
      .filter((dropDownOption) => dropDownOption.checked)
      ?.map((order) => order.value);
    if (!verifiedOrders?.length && !assignedOrders?.length) {
      this.toastService.showToast({ message: 'Please select order numbers', mode: 'danger' });
    } else if (verifiedOrders?.some((orderId) => assignedOrders?.includes(orderId))) {
      const issueOrdersIds = verifiedOrders.filter((orderId) => assignedOrders.includes(orderId));
      const issueOrdersNumbers = this.posibbleAssignOrders
        .filter((order) => issueOrdersIds.includes(order.value))
        ?.map((order) => order.label);
      this.toastService.showToast({
        message: `Unable to select orders for both verify and assign screens  : ${JSON.stringify(issueOrdersNumbers)}`,
        mode: 'danger',
        duration: TimeUnits._SECOND_IN_MS * 10,
      });
    } else {
      this.dialogRef.close({ validOrders: true, verifiedOrders, assignedOrders });
    }
  }
}
